'use client';

import { IconChevronLeftFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-left-filled';
import { interactionResponse } from '@zillow/yield-callback';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { photoCarouselNavButtonRecipe } from './styles/photo-carousel-nav-button.styles';
import { photoCarouselPreviousButtonStyles } from './styles/photo-carousel-previous-button.styles';
import { handOffFocusToSibling } from './utils/hand-off-focus-to-sibling';
import type { PhotoCarouselPreviousButtonPropsInterface } from './types/photo-carousel-previous-button.types';

/**
 * Control used to show the previous photo in the carousel.
 *
 * Along with `PhotoCarousel.NextButton`, this is the keyboard interface for the
 * carousel — the dots are decorative [CDS-5858].
 *
 * @related ['IconButton']
 * @since 10.0.0
 */

export const PhotoCarouselPreviousButton = forwardRef<
  HTMLButtonElement,
  PhotoCarouselPreviousButtonPropsInterface
>((props: PhotoCarouselPreviousButtonPropsInterface, forwardedRef) => {
  const {
    activeIndex = 0,
    shouldAwaitInteractionResponse,
    shouldLoop = true,
    updateActiveIndex,
  } = useContext(PhotoCarouselRootContext);
  const {
    children = (
      <Icon>
        <IconChevronLeftFilled />
      </Icon>
    ),
    css: cssProp,
    disabled = !shouldLoop && activeIndex === 0,
    onClick,
    ...rest
  } = props;

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      // Stop propagation immediately to prevent invoking Property Card's `onClick`
      event.stopPropagation();

      if (disabled) return;

      // Captured before the `await` below, after which React has reset `currentTarget`.
      const button = event.currentTarget;

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      const nextIndex = activeIndex - 1;

      updateActiveIndex?.(nextIndex);

      if (!shouldLoop && nextIndex === 0) {
        handOffFocusToSibling(button, 'c11n_photo-carousel__next-button');
      }

      onClick?.(event);
    },
    [activeIndex, disabled, onClick, shouldAwaitInteractionResponse, shouldLoop, updateActiveIndex],
  );

  return (
    <IconButton
      data-c11n-component="PhotoCarousel.PreviousButton"
      disabled={disabled}
      emphasis="bare"
      onClick={handleClick}
      onImpact
      ref={forwardedRef}
      title="Previous photo"
      tone="neutral"
      css={css.raw(
        photoCarouselNavButtonRecipe.raw({ disabled }),
        photoCarouselPreviousButtonStyles,
        cssProp,
      )}
      {...rest}
      className={cx('c11n_photo-carousel__previous-button', rest.className)}
    >
      {children}
    </IconButton>
  );
});

PhotoCarouselPreviousButton.displayName = 'PhotoCarousel.PreviousButton';
